import {Button, ButtonProps, Chip, Theme as MuiTheme, Tooltip, useTheme} from '@mui/material';
import {CSSProperties} from '@mui/styles';
import makeStyles from '@mui/styles/makeStyles';
import {default as classNames, default as classnames} from 'classnames';
import {useCallback, useMemo, useState} from 'react';
import Select, {ActionMeta, Theme} from 'react-select';
import CreatableSelect from 'react-select/creatable';
import {InputActionMeta} from 'react-select/src/types';
import {useScrollbarStyles} from 'src/shared/styles/scrollbarStyles';
import {customStyles} from 'src/view/components/auto-complete/constants';
import {
    AutoCompleteOption,
    AutoCompleteOptions,
    AutoCompleteValueOption,
} from 'src/view/components/auto-complete/interfaces';

const useStyles = makeStyles((theme: MuiTheme) => ({
    selectContainer: {
        display: 'flex',
        flex: 1,
    },
    autoComplete: {
        flex: 1,
    },
    selectionsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(0.5),
        marginBottom: theme.spacing(1),
    },
    button: {
        background: theme.colors.white,
        border: `1px solid #0000003b`,
        borderLeft: 0,
        boxShadow: 'none',
        textTransform: 'capitalize',
        color: theme.palette.primary.main,
        fontSize: theme.spacing(1.5),
        borderRadius: `0 ${theme.layout.borderRadius.regular}px ${theme.layout.borderRadius.regular}px 0`,
        '& svg': {
            fill: theme.colors.grey,
        },
    },
}));

export type AutoCompleteOnChange = (
    value: AutoCompleteValueOption,
    action?: ActionMeta<AutoCompleteOption>
) => void;

export interface AutoCompleteProps {
    buttonProps?: ButtonProps;
    placeholder?: string;
    options: AutoCompleteOptions;
    value?: AutoCompleteValueOption;
    isClearable?: boolean;
    onChange: AutoCompleteOnChange;
    onCreateOption?: ((value: string) => void) | ((value: string) => Promise<void>);
    disabled?: boolean;
    name: string;
    backgroundColor?: CSSProperties['color'];
    isMulti?: boolean;
    loading?: boolean;
    onInputChange?: (newValue: string, actionMeta: InputActionMeta) => void;
    enableLabelTooltip?: boolean;
    /**
     * Multi-select only. When set, selected values are not rendered as chips inside the
     * input control but as removable chips above it. Keeps the control on a single line
     * regardless of how many options are selected.
     */
    selectionsAbove?: boolean;
    /**
     * Multi-select only. Option values that cannot be removed by the user: their chips are
     * rendered without a delete button. Note this only governs the chip UI; callers that need
     * to guarantee these stay selected must also enforce it in their onChange handler.
     */
    lockedValues?: Array<string | number>;
}

export default function AutoComplete({
                                         buttonProps,
                                         options,
                                         placeholder,
                                         value,
                                         onChange,
                                         onCreateOption,
                                         disabled,
                                         isClearable = true,
                                         name,
                                         backgroundColor,
                                         isMulti,
                                         loading,
                                         onInputChange,
                                         enableLabelTooltip,
                                         selectionsAbove,
                                         lockedValues,
                                         ...props
                                     }: AutoCompleteProps): JSX.Element {
    const classes = useStyles();
    const scrollbarStyles = useScrollbarStyles();
    const theme = useTheme<MuiTheme>();
    const [openTooltip, setOpenTooltip] = useState(false);

    const themeConfig = useCallback(
        (themeConfig: Theme) => ({
            ...themeConfig,
            colors: {
                ...themeConfig.colors,
                primary: theme.palette.primary.main,
                primary25: theme.palette.primary.light,
            },
        }),
        [theme]
    );

    const selectStyles = useMemo(
        () => customStyles(theme, !!buttonProps, !disabled ? backgroundColor : undefined),
        [theme, buttonProps, disabled, backgroundColor]
    );

    // react-select treats undefined as uncontrolled; null is required to actually clear the selection.
    const selectValue = value ?? null;

    const onChangeValue = (e: AutoCompleteOnChange) => {
        onChange(e);

        if (enableLabelTooltip && openTooltip) {
            setOpenTooltip(false);
        }
    };

    const showSelectionsAbove = selectionsAbove && isMulti;
    const selectedOptions = (Array.isArray(value) ? value : []) as AutoCompleteOption[];

    const onRemoveSelection = (option: AutoCompleteOption) => {
        const remaining = selectedOptions.filter((o) => o.value !== option.value);
        onChange(remaining as AutoCompleteValueOption);
    };

    const renderSelectionsAbove = () =>
        showSelectionsAbove &&
        selectedOptions.length > 0 && (
            <div className={classes.selectionsContainer}>
                {selectedOptions.map((option) => (
                    <Chip
                        key={`${name}-${option.value}`}
                        label={option.label}
                        onDelete={
                            disabled || lockedValues?.includes(option.value)
                                ? undefined
                                : () => onRemoveSelection(option)
                        }
                        size="small"
                    />
                ))}
            </div>
        );

    const renderAutoComplete = () => {
        return (
            <>
                {!onCreateOption && (
                    <Select
                        key={`key-${name}`}
                        isDisabled={disabled}
                        menuPosition="fixed"
                        theme={themeConfig}
                        onChange={onChangeValue}
                        value={selectValue}
                        styles={selectStyles}
                        placeholder={placeholder}
                        options={options}
                        isClearable={isClearable}
                        isMulti={isMulti}
                        controlShouldRenderValue={!showSelectionsAbove}
                        isLoading={loading}
                        onInputChange={onInputChange}
                        filterOption={filterByLabelOnly}
                    />
                )}

                {onCreateOption && (
                    <CreatableSelect
                        key={`key-${name}`}
                        isDisabled={disabled}
                        menuPosition="fixed"
                        theme={themeConfig}
                        onChange={onChangeValue}
                        value={selectValue}
                        styles={selectStyles}
                        placeholder={placeholder}
                        options={options}
                        isClearable={isClearable}
                        onCreateOption={onCreateOption}
                        isMulti={isMulti}
                        controlShouldRenderValue={!showSelectionsAbove}
                        isLoading={loading}
                        onInputChange={onInputChange}
                        filterOption={filterByLabelOnly}
                    />
                )}
            </>
        );
    };

    return (
        <>
            {renderSelectionsAbove()}
            <div
                className={classnames(classes.selectContainer, scrollbarStyles.hideScrollbar)}
                onMouseEnter={() => enableLabelTooltip && setOpenTooltip(true)}
                onMouseLeave={() => enableLabelTooltip && setOpenTooltip(false)}
                {...props}
            >
                {enableLabelTooltip && value ? (
                    <Tooltip title={value?.label} open={openTooltip} placement="top-start">
                        <div style={{width: '100%'}}>{renderAutoComplete()}</div>
                    </Tooltip>
                ) : (
                    renderAutoComplete()
                )}

                {buttonProps && (
                    <Button
                        {...buttonProps}
                        className={classNames(classes.button, buttonProps.className)}
                        disableRipple
                    >
                        {buttonProps.children}
                    </Button>
                )}
            </div>
        </>
    );
}

/**
 * A helper function that filters options based on their label using the createFilter function from react-select.
 * This function is designed to filter options by label only, ignoring the value.
 * It's particularly useful when the value represents a database ID, which users typically don't want to filter by.
 *
 * For example:
 * - Suppose a user wants to find a block named "block-7".
 * - The user types "7" into the input field
 * - Thanks to this function, the criteria returns only the block with { label: 'block-7', value: 'database-id-dont-matter' } and excludes { label: 'block-15', value: 'id-containing-**7**' }.
 *
 * This behavior is discussed in detail at: https://github.com/JedWatson/react-select/issues/3403
 */
const filterByLabelOnly = (option: AutoCompleteOption, inputValue: string): boolean =>
    (option.label.toString().toLowerCase().match(inputValue.toLowerCase()) || []).length > 0;
